import { Link } from "react-router-dom";
import { useApp } from "../store";
import { Empty, Header, Note, SectionLabel, StateBadge, formatPrice } from "../ui";

/**
 * Payments: one subscription per number, which is the billing model. There is no wallet and no per-use
 * charge, so this page is a list of per-number subscriptions rather than a balance.
 */
export function PaymentsPage(): JSX.Element {
  const { numbers } = useApp();
  const billable = numbers.filter((number) => number.state !== "RELEASED");

  return (
    <>
      <Header title="Payments" />
      <div className="content">
        <SectionLabel>Subscriptions (per number)</SectionLabel>
        {billable.length === 0 ? (
          <Empty>No subscriptions yet.</Empty>
        ) : (
          <ul className="list">
            {billable.map((number) => (
              <li key={number.id}>
                <div className="row-main">
                  <div className="row-title">{number.e164}</div>
                  <div className="row-sub">
                    {formatPrice(number.monthlyAmountMinor, number.currency)}
                    {number.state === "DISABLED" && number.subscription?.paymentFailedAt
                      ? ` \u2014 payment failed ${new Date(
                          number.subscription.paymentFailedAt
                        ).toLocaleDateString()}`
                      : number.subscription?.currentTermEnd
                        ? ` \u2014 renews ${new Date(
                            number.subscription.currentTermEnd
                          ).toLocaleDateString()}`
                        : ""}
                  </div>
                  {number.state === "DISABLED" && number.subscription?.graceExpiresAt ? (
                    <div className="row-sub">
                      Released after {new Date(number.subscription.graceExpiresAt).toLocaleString()}
                    </div>
                  ) : null}
                </div>
                <StateBadge state={number.state} />
              </li>
            ))}
          </ul>
        )}

        <SectionLabel>Payment method</SectionLabel>
        <ul className="list">
          <li>
            <div className="avatar sq">{"\u{1F4B3}"}</div>
            <div className="row-main">
              <div className="row-title">Managed by the payment provider</div>
              <div className="row-sub">Cards are entered on the provider's hosted pages</div>
            </div>
            <Link className="back" to="/payments/method">
              Details
            </Link>
          </li>
        </ul>

        <Note>
          No per-call or per-message charges. Each number carries a high fair-use cap enforced by counters.
        </Note>
      </div>
    </>
  );
}

/**
 * Payment methods. Deliberately thin: the backend stores no card data, and the provider's hosted page is
 * where a card is entered, so there is nothing here to edit.
 */
export function PaymentMethodPage(): JSX.Element {
  return (
    <>
      <Header title="Payment methods" back="/payments" backLabel={"\u2190 Payments"} />
      <div className="content">
        <Note>
          Payment methods are handled entirely by the payment provider. The backend never sees or stores
          card data; it only learns about payment state from provider webhooks.
        </Note>
        <SectionLabel>How a card is added</SectionLabel>
        <ul className="list">
          <li>
            <div className="avatar sq">1</div>
            <div className="row-main">
              <div className="row-title">Buy a number</div>
              <div className="row-sub">A hosted checkout session is created for the plan</div>
            </div>
          </li>
          <li>
            <div className="avatar sq">2</div>
            <div className="row-main">
              <div className="row-title">Pay on the provider's page</div>
              <div className="row-sub">The card is stored with the provider, against your customer id</div>
            </div>
          </li>
          <li>
            <div className="avatar sq">3</div>
            <div className="row-main">
              <div className="row-title">Renewals reuse it</div>
              <div className="row-sub">A failed renewal disables the number and starts the grace period</div>
            </div>
          </li>
        </ul>
        <Link className="btn secondary" to="/numbers/buy">
          Buy a number
        </Link>
      </div>
    </>
  );
}
